import React, { useState } from 'react';
import { X, Plus, Trash2, Calendar, Tag, FileText, Users } from 'lucide-react';

const CreateProjectModal = ({ show, onClose, onSubmit }) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        startDate: '',
        endDate: '',
        tags: []
    });
    const [newTag, setNewTag] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleAddTag = () => {
        if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
            setFormData(prev => ({
                ...prev,
                tags: [...prev.tags, newTag.trim()]
            }));
            setNewTag('');
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter(tag => tag !== tagToRemove)
        }));
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddTag();
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Project name is required';
        }

        if (!formData.description.trim()) {
            newErrors.description = 'Project description is required';
        }

        if (!formData.startDate) {
            newErrors.startDate = 'Start date is required';
        }

        if (!formData.endDate) {
            newErrors.endDate = 'End date is required';
        }

        if (formData.startDate && formData.endDate) {
            const startDate = new Date(formData.startDate);
            const endDate = new Date(formData.endDate);

            if (startDate >= endDate) {
                newErrors.endDate = 'End date must be after start date';
            }
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setIsSubmitting(true);

        try {
            const result = await onSubmit(formData);
            if (result.success) {
                // Reset form
                setFormData({
                    name: '',
                    description: '',
                    startDate: '',
                    endDate: '',
                    tags: []
                });
                setErrors({});
                onClose();
            } else {
                setErrors({ submit: result.message || 'Failed to create project' });
            }
        } catch (error) {
            setErrors({ submit: error.message || 'An error occurred' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setFormData({
            name: '',
            description: '',
            startDate: '',
            endDate: '',
            tags: []
        });
        setErrors({});
        setNewTag('');
        onClose();
    };

    if (!show) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-container">
                <div className="modal-header">
                    <h2 className="modal-title">
                        <Plus style={{ width: '20px', height: '20px' }} />
                        Create New Project
                    </h2>
                    <button
                        className="modal-close"
                        onClick={handleClose}
                        disabled={isSubmitting}
                    >
                        <X style={{ width: '20px', height: '20px' }} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="modal-form">
                    <div className="form-group">
                        <label htmlFor="name" className="form-label">
                            <FileText style={{ width: '16px', height: '16px' }} />
                            Project Name *
                        </label>
                        <input
                            id="name"
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            className={`form-input ${errors.name ? 'error' : ''}`}
                            placeholder="Enter project name..."
                            disabled={isSubmitting}
                        />
                        {errors.name && <span className="error-text">{errors.name}</span>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="description" className="form-label">
                            <FileText style={{ width: '16px', height: '16px' }} />
                            Description *
                        </label>
                        <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            className={`form-textarea ${errors.description ? 'error' : ''}`}
                            placeholder="Describe your project..."
                            rows="4"
                            disabled={isSubmitting}
                        />
                        {errors.description && <span className="error-text">{errors.description}</span>}
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="startDate" className="form-label">
                                <Calendar style={{ width: '16px', height: '16px' }} />
                                Start Date *
                            </label>
                            <input
                                id="startDate"
                                type="date"
                                name="startDate"
                                value={formData.startDate}
                                onChange={handleInputChange}
                                className={`form-input ${errors.startDate ? 'error' : ''}`}
                                disabled={isSubmitting}
                            />
                            {errors.startDate && <span className="error-text">{errors.startDate}</span>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="endDate" className="form-label">
                                <Calendar style={{ width: '16px', height: '16px' }} />
                                End Date *
                            </label>
                            <input
                                id="endDate"
                                type="date"
                                name="endDate"
                                value={formData.endDate}
                                onChange={handleInputChange}
                                className={`form-input ${errors.endDate ? 'error' : ''}`}
                                disabled={isSubmitting}
                            />
                            {errors.endDate && <span className="error-text">{errors.endDate}</span>}
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label">
                            <Tag style={{ width: '16px', height: '16px' }} />
                            Tags (Optional)
                        </label>
                        <div className="tags-input-container">
                            <input
                                type="text"
                                value={newTag}
                                onChange={(e) => setNewTag(e.target.value)}
                                onKeyPress={handleKeyPress}
                                className="form-input"
                                placeholder="Add a tag..."
                                disabled={isSubmitting}
                            />
                            <button
                                type="button"
                                onClick={handleAddTag}
                                className="add-tag-button"
                                disabled={!newTag.trim() || isSubmitting}
                            >
                                <Plus style={{ width: '16px', height: '16px' }} />
                            </button>
                        </div>

                        {formData.tags.length > 0 && (
                            <div className="tags-display">
                                {formData.tags.map((tag, index) => (
                                    <span key={index} className="tag-item">
                                        {tag}
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveTag(tag)}
                                            className="remove-tag-button"
                                            disabled={isSubmitting}
                                        >
                                            <Trash2 style={{ width: '12px', height: '12px' }} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {errors.submit && (
                        <div className="error-message">
                            {errors.submit}
                        </div>
                    )}

                    <div className="modal-footer">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="button secondary"
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="button primary"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="spinner"></div>
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <Plus style={{ width: '16px', height: '16px' }} />
                                    Create Project
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateProjectModal;